import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { FiPlus, FiSearch, FiEye, FiDownload } from 'react-icons/fi';
import { BACKEND_URL as BACKEND } from '../config';

export default function Bills() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/bills', {
        params: { search, status: statusFilter, startDate, endDate, page, limit: 20 }
      });
      setBills(res.data.bills);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch { toast.error('Failed to load bills'); }
    setLoading(false);
  }, [search, statusFilter, startDate, endDate, page]);

  useEffect(() => { fetchBills(); }, [fetchBills]);

  const downloadPDF = (id) => {
    window.open(`${BACKEND}/api/bills/${id}/pdf`, '_blank');
  };

  const statusBadge = (status) => {
    const map = { paid: 'badge-success', pending: 'badge-warning', partial: 'badge-info' };
    return <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div className="filter-tabs">
          {['all', 'pending', 'partial', 'paid'].map(s => (
            <button key={s} className={`filter-tab ${statusFilter === s ? 'active' : ''}`}
              onClick={() => { setStatusFilter(s); setPage(1); }}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <Link to="/bills/create" className="btn btn-accent">
          <FiPlus size={15} /> Create Bill
        </Link>
      </div>

      <div className="card">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: 10 }}>
          <span style={{ color: '#64748b', fontSize: 13 }}>{total} bills</span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="date" className="form-control" style={{ width: 140 }}
              value={startDate} onChange={e => setStartDate(e.target.value)} />
            <span style={{ color: '#94a3b8' }}>to</span>
            <input type="date" className="form-control" style={{ width: 140 }}
              value={endDate} onChange={e => setEndDate(e.target.value)} />
            <div className="search-input-wrap" style={{ width: 220 }}>
              <FiSearch size={14} />
              <input className="form-control" placeholder="Search bill, customer..."
                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
          </div>
        </div>

        {loading ? <div className="loading"><div className="spinner"></div></div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Bill #</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Total</th>
                  <th>Advance</th>
                  <th>Remaining</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bills.length === 0 && (
                  <tr><td colSpan={8}>
                    <div className="empty-state">
                      <div className="empty-state-icon">🧾</div>
                      <h3>No bills found</h3>
                      <p>Create your first bill to get started</p>
                    </div>
                  </td></tr>
                )}
                {bills.map(bill => (
                  <tr key={bill._id}>
                    <td>
                      <span style={{ fontWeight: 700, color: '#3b82f6', cursor: 'pointer' }}
                        onClick={() => navigate(`/bills/${bill._id}`)}>
                        #{bill.billNumber}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{bill.customerName}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{bill.customerPhone}</div>
                    </td>
                    <td>{new Date(bill.billDate).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td><strong>SAR {bill.total?.toFixed(0)}</strong></td>
                    <td style={{ color: '#10b981' }}>SAR {bill.advancePaid?.toFixed(0)}</td>
                    <td style={{ color: bill.remaining > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>
                      SAR {bill.remaining?.toFixed(0)}
                    </td>
                    <td>{statusBadge(bill.status)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-outline btn-sm btn-icon" title="View"
                          onClick={() => navigate(`/bills/${bill._id}`)}>
                          <FiEye size={14} />
                        </button>
                        <button className="btn btn-outline btn-sm btn-icon" title="Download PDF"
                          onClick={() => downloadPDF(bill._id)}>
                          <FiDownload size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div style={{ padding: '12px 16px' }}>
            <div className="pagination">
              {Array.from({ length: pages }, (_, i) => (
                <button key={i} className={`page-btn ${page === i + 1 ? 'active' : ''}`}
                  onClick={() => setPage(i + 1)}>{i + 1}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}